import React, { useEffect, useState, useMemo } from "react";
import { 
  Alert, 
  Box, 
  Button, 
  CircularProgress, 
  Container, 
  Paper, 
  Stack, 
  Typography, 
  Grid, 
  Fade,
  Autocomplete,
  TextField,
  Chip
} from "@mui/material";
import { useParams } from "react-router-dom";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PublicIcon from "@mui/icons-material/Public";
import API from "../../utils/Axios.utils";

const COMMON_TIMEZONES = [
  { label: "US - Eastern (New York, EDT/EST)", value: "America/New_York" },
  { label: "US - Central (Chicago, CDT/CST)", value: "America/Chicago" },
  { label: "US - Mountain (Denver, MDT/MST)", value: "America/Denver" },
  { label: "US - Pacific (Los Angeles, PDT/PST)", value: "America/Los_Angeles" },
  { label: "Australia - Sydney, Melbourne (AEST/AEDT)", value: "Australia/Sydney" },
  { label: "Australia - Brisbane (AEST)", value: "Australia/Brisbane" },
  { label: "Australia - Perth (AWST)", value: "Australia/Perth" },
  { label: "Australia - Adelaide (ACST/ACDT)", value: "Australia/Adelaide" },
  { label: "India - Standard Time (IST)", value: "Asia/Kolkata" },
  { label: "UK - London (GMT/BST)", value: "Europe/London" },
  { label: "Europe - Paris, Berlin, Amsterdam (CET/CEST)", value: "Europe/Paris" },
  { label: "UAE - Dubai (GST)", value: "Asia/Dubai" },
  { label: "Singapore - Standard Time (SGT)", value: "Asia/Singapore" },
  { label: "Japan - Tokyo (JST)", value: "Asia/Tokyo" },
  { label: "Canada - Toronto (EDT/EST)", value: "America/Toronto" },
  { label: "Canada - Vancouver (PDT/PST)", value: "America/Vancouver" },
  { label: "New Zealand - Auckland (NZST/NZDT)", value: "Pacific/Auckland" },
  { label: "Universal Coordinated Time (UTC)", value: "UTC" },
];

const CandidateInterviewSelection = () => {
  const { token } = useParams();
  const [interview, setInterview] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedDayIndex, setSelectedDayIndex] = useState(0);

  // Auto-detect candidate local timezone
  const detectedTimezone = useMemo(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Kolkata";
    } catch {
      return "Asia/Kolkata";
    }
  }, []);

  const [candidateTz, setCandidateTz] = useState(detectedTimezone);

  // Ensure detected timezone is in the list
  const timezoneOptions = useMemo(() => {
    const exists = COMMON_TIMEZONES.some((tz) => tz.value === candidateTz);
    if (!exists && candidateTz) {
      return [{ label: `${candidateTz} (Local)`, value: candidateTz }, ...COMMON_TIMEZONES];
    }
    return COMMON_TIMEZONES;
  }, [candidateTz]);

  const selectSlot = async (slotIdToSelect = selectedSlot) => {
    if (!slotIdToSelect) return;
    setSubmitting(true);
    setMessage(null);
    try {
      const response = await API.post(`api/interview/public/${token}/select-slot`, { starts_at: slotIdToSelect });
      setInterview((current) => ({ ...current, slots: [], status: "SLOT_SELECTED", confirmation: response?.data }));
      setMessage({ type: "success", text: "Your interview slot has been reserved. A confirmation email with calendar invitation will follow." });
    } catch (error) {
      setMessage({ type: "error", text: error?.response?.data?.message || error?.message || "Failed to book the slot. Please refresh and try again." });
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    API.get(`api/interview/public/${token}`)
      .then((response) => {
        const interviewData = response?.data || null;
        setInterview(interviewData);
        
        if (interviewData) {
          const searchParams = new URLSearchParams(window.location.search);
          const autoSelectTime = searchParams.get('autoSelectTime');
          
          if (autoSelectTime && interviewData.calendar) {
            for (let i = 0; i < interviewData.calendar.length; i++) {
              const day = interviewData.calendar[i];
              const slot = day.slots.find(s => new Date(s.starts_at).getTime() === parseInt(autoSelectTime));
              if (slot && slot.status === 'AVAILABLE') {
                setSelectedSlot(slot.starts_at);
                break;
              }
            }
          }
        }
      })
      .catch((error) => setMessage({ type: "error", text: error?.response?.data?.message || "This interview invitation is invalid or expired." }))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Dynamically regroup all slots into the Candidate's selected Timezone
  const dynamicCalendar = useMemo(() => {
    if (!interview?.calendar) return [];

    // Flatten all slots
    const allSlots = [];
    interview.calendar.forEach((day) => {
      if (Array.isArray(day.slots)) {
        day.slots.forEach((s) => allSlots.push(s));
      }
    });

    const dayMap = new Map();

    allSlots.forEach((slot) => {
      const slotDate = new Date(slot.starts_at);
      if (isNaN(slotDate.getTime())) return;

      // Determine date key in candidate timezone (yyyy-MM-dd)
      const dateKey = new Intl.DateTimeFormat("en-CA", {
        timeZone: candidateTz,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).format(slotDate);

      if (!dayMap.has(dateKey)) {
        dayMap.set(dateKey, {
          date: dateKey,
          representativeDate: slotDate,
          slots: [],
        });
      }
      dayMap.get(dateKey).slots.push(slot);
    });

    // Sort days chronologically
    return Array.from(dayMap.values()).sort((a, b) => a.date.localeCompare(b.date));
  }, [interview, candidateTz]);

  // Reset selectedDayIndex if out of range
  useEffect(() => {
    if (selectedDayIndex >= dynamicCalendar.length) {
      setSelectedDayIndex(0);
    }
  }, [dynamicCalendar, selectedDayIndex]);

  const formatDayName = (dateObj) => {
    try {
      return new Intl.DateTimeFormat("en-US", { timeZone: candidateTz, weekday: "short" }).format(dateObj);
    } catch {
      return "";
    }
  };

  const formatDayNumber = (dateObj) => {
    try {
      return new Intl.DateTimeFormat("en-US", { timeZone: candidateTz, day: "numeric" }).format(dateObj);
    } catch {
      return "";
    }
  };

  const formatMonthName = (dateObj) => {
    try {
      return new Intl.DateTimeFormat("en-US", { timeZone: candidateTz, month: "short" }).format(dateObj);
    } catch {
      return "";
    }
  };

  const formatSlotTime = (isoString) => {
    try {
      return new Intl.DateTimeFormat("en-US", {
        timeZone: candidateTz,
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      }).format(new Date(isoString));
    } catch {
      return new Date(isoString).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
    }
  };

  if (loading) return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" sx={{ background: 'linear-gradient(135deg, #fdfbfb 0%, #ebedee 100%)' }}>
      <CircularProgress size={60} thickness={4} sx={{ color: '#0f9d78' }} />
    </Box>
  );

  return (
    <Box sx={{ minHeight: "100vh", py: { xs: 2, sm: 4 }, display: 'flex', flexDirection: 'column', justifyContent: 'center', background: 'linear-gradient(135deg, #f6f8fb 0%, #e9edf3 100%)' }}>
      <Container maxWidth="lg">
        <Fade in={true} timeout={800}>
          <Paper elevation={0} sx={{ 
            p: { xs: 3, sm: 4 }, 
            borderRadius: 4, 
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.6)',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.05)'
          }}>
            <Stack spacing={2.5}>
              <Box textAlign="center" sx={{ mb: 0 }}>
                <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 56, height: 56, borderRadius: '50%', background: 'linear-gradient(135deg, #12304a 0%, #2b557a 100%)', color: '#fff', mb: 1.5, boxShadow: '0 10px 20px rgba(18, 48, 74, 0.2)' }}>
                  <CalendarTodayIcon fontSize="medium" />
                </Box>
                <Typography variant="h4" component="h1" fontWeight="800" sx={{ background: 'linear-gradient(90deg, #12304a 0%, #2b557a 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', mb: 0.5 }}>
                  Schedule Your Interview
                </Typography>
                {interview && (
                  <Stack direction="row" spacing={1} justifyContent="center" alignItems="center" flexWrap="wrap" sx={{ mt: 1 }}>
                    <Chip 
                      label={interview.stage?.replaceAll("_", " ")} 
                      size="small" 
                      sx={{ bgcolor: '#e2e8f0', fontWeight: 600, color: '#1e293b' }} 
                    />
                    <Chip 
                      icon={<AccessTimeIcon fontSize="small" />} 
                      label={`${interview.duration_minutes} min`} 
                      size="small" 
                      sx={{ bgcolor: '#e2e8f0', fontWeight: 600, color: '#1e293b' }} 
                    />
                    <Chip 
                      label={`Host Timezone: ${interview.timezone || 'Asia/Kolkata'}`} 
                      size="small" 
                      variant="outlined"
                      sx={{ borderColor: '#cbd5e1', fontWeight: 500, color: '#475569' }} 
                    />
                  </Stack>
                )}
              </Box>

              {/* Timezone Selector Section */}
              {interview?.calendar && interview.calendar.length > 0 && interview.status !== 'CONFIRMED' && (
                <Paper elevation={0} sx={{ p: 2, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 3 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={7}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <PublicIcon sx={{ color: '#2b557a', fontSize: 22 }} />
                        <Box>
                          <Typography variant="subtitle2" fontWeight={700} color="#1e293b">
                            Your Viewing Timezone
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Slots below are automatically translated to your local time.
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={5}>
                      <Autocomplete
                        size="small"
                        options={timezoneOptions}
                        getOptionLabel={(option) => typeof option === 'string' ? option : option.label}
                        value={timezoneOptions.find(o => o.value === candidateTz) || { label: candidateTz, value: candidateTz }}
                        onChange={(_, newValue) => {
                          if (newValue) {
                            setCandidateTz(typeof newValue === 'string' ? newValue : newValue.value);
                          }
                        }}
                        renderInput={(params) => (
                          <TextField {...params} label="Timezone" placeholder="Select timezone" />
                        )}
                        disableClearable
                      />
                    </Grid>
                  </Grid>
                </Paper>
              )}

              {message && (
                <Alert 
                  severity={message.type} 
                  icon={message.type === 'success' ? <CheckCircleIcon fontSize="inherit" /> : undefined}
                  sx={{ borderRadius: 2, '& .MuiAlert-message': { fontWeight: 500, fontSize: '1.05rem' } }}
                >
                  {message.text}
                </Alert>
              )}

              {interview?.meeting_link && (
                <Box textAlign="center">
                  <Button 
                    variant="contained" 
                    component="a" 
                    href={interview.meeting_link} 
                    target="_blank" 
                    rel="noreferrer"
                    sx={{ 
                      borderRadius: 8, 
                      px: 4, py: 1.5, 
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      textTransform: 'none',
                      background: 'linear-gradient(135deg, #12304a 0%, #2b557a 100%)',
                      boxShadow: '0 8px 16px rgba(18, 48, 74, 0.2)',
                      transition: 'transform 0.2s',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 12px 20px rgba(18, 48, 74, 0.3)'
                      }
                    }}
                  >
                    Join Confirmed Interview
                  </Button>
                </Box>
              )}

              {dynamicCalendar.length > 0 && interview.status !== 'CONFIRMED' && (
                <Box sx={{ mt: 0 }}>
                  <Typography variant="h6" fontWeight="700" sx={{ mb: 2, color: '#12304a', textAlign: 'center' }}>
                    Select a Date & Time
                  </Typography>
                  
                  {/* Day Selector */}
                  <Stack direction="row" spacing={1.5} sx={{ overflowX: 'auto', pb: 1, justifyContent: { md: 'center' } }}>
                    {dynamicCalendar.map((day, idx) => (
                      <Button
                        key={day.date}
                        variant={selectedDayIndex === idx ? "contained" : "outlined"}
                        onClick={() => setSelectedDayIndex(idx)}
                        sx={{ 
                          minWidth: 85, 
                          borderRadius: 3, 
                          py: 1.5,
                          textTransform: 'none',
                          background: selectedDayIndex === idx ? 'linear-gradient(135deg, #12304a 0%, #2b557a 100%)' : '#fff',
                          borderColor: selectedDayIndex === idx ? 'transparent' : '#e2e8f0',
                          color: selectedDayIndex === idx ? '#fff' : '#475569',
                          boxShadow: selectedDayIndex === idx ? '0 4px 12px rgba(18, 48, 74, 0.2)' : '0 2px 4px rgba(0,0,0,0.02)',
                          '&:hover': {
                            background: selectedDayIndex === idx ? 'linear-gradient(135deg, #12304a 0%, #2b557a 100%)' : '#f8fafc',
                            borderColor: selectedDayIndex === idx ? 'transparent' : '#cbd5e1',
                          }
                        }}
                      >
                        <Box>
                          <Typography variant="caption" display="block" fontWeight="700" sx={{ opacity: 0.9, letterSpacing: 1, textTransform: 'uppercase' }}>
                            {formatDayName(day.representativeDate)}
                          </Typography>
                          <Typography variant="h5" fontWeight="800" sx={{ mt: 0.5 }}>
                            {formatDayNumber(day.representativeDate)}
                          </Typography>
                          <Typography variant="caption" display="block" sx={{ opacity: 0.8, fontSize: '0.7rem' }}>
                            {formatMonthName(day.representativeDate)}
                          </Typography>
                        </Box>
                      </Button>
                    ))}
                  </Stack>

                  {/* Time Slots for Selected Day */}
                  <Box sx={{ mt: 1.5, p: { xs: 2, sm: 2.5 }, background: '#f8fafc', borderRadius: 4, border: '1px solid #e2e8f0' }}>
                    <Grid container spacing={1.5}>
                      {dynamicCalendar[selectedDayIndex]?.slots.map((slot) => {
                        const isSelected = selectedSlot === slot.starts_at;
                        const isAvailable = slot.status === "AVAILABLE";
                        const isBusy = slot.status !== "AVAILABLE";
                        
                        return (
                          <Grid item xs={6} sm={4} md={3} key={slot.starts_at}>
                            <Button
                              variant="outlined"
                              fullWidth
                              disabled={!isAvailable}
                              onClick={() => setSelectedSlot(slot.starts_at)}
                              sx={{
                                py: 1.2,
                                borderRadius: 2,
                                textTransform: "none",
                                fontWeight: 600,
                                fontSize: '1rem',
                                borderWidth: isSelected ? 2 : 1,
                                borderColor: isSelected ? '#2b557a' : isAvailable ? '#cbd5e1' : '#e2e8f0',
                                color: isSelected ? '#fff' : isAvailable ? '#334155' : '#94a3b8',
                                background: isSelected ? 'linear-gradient(135deg, #12304a 0%, #2b557a 100%)' : isAvailable ? '#fff' : '#f1f5f9',
                                boxShadow: isSelected ? '0 4px 12px rgba(18, 48, 74, 0.2)' : 'none',
                                transition: 'all 0.2s',
                                '&:hover': {
                                  borderWidth: isSelected ? 2 : 1,
                                  borderColor: isAvailable && !isSelected ? '#2b557a' : undefined,
                                  background: isAvailable && !isSelected ? '#e2e8f0' : undefined,
                                  transform: isAvailable && !isSelected ? 'translateY(-2px)' : 'none',
                                  boxShadow: isAvailable && !isSelected ? '0 4px 8px rgba(0,0,0,0.05)' : undefined
                                },
                                '&.Mui-disabled': {
                                  color: isBusy && slot.status === 'BOOKED' ? '#ef4444' : '#94a3b8',
                                  background: isBusy && slot.status === 'BOOKED' ? '#fee2e2' : '#f1f5f9',
                                  borderColor: isBusy && slot.status === 'BOOKED' ? '#fca5a5' : '#e2e8f0',
                                  opacity: isBusy && slot.status === 'BOOKED' ? 0.7 : 0.5
                                }
                              }}
                            >
                              {formatSlotTime(slot.starts_at)}
                            </Button>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Box>
                </Box>
              )}

              {dynamicCalendar.length > 0 && interview.status !== 'CONFIRMED' && (
                <Box textAlign="center" sx={{ pt: 1 }}>
                  <Button 
                    variant="contained" 
                    size="large"
                    disabled={!selectedSlot || submitting} 
                    onClick={() => selectSlot()}
                    sx={{
                      borderRadius: 8,
                      px: 5,
                      py: 1.2,
                      fontSize: '1.1rem',
                      fontWeight: 700,
                      textTransform: 'none',
                      background: 'linear-gradient(135deg, #12304a 0%, #2b557a 100%)',
                      boxShadow: '0 10px 20px rgba(18, 48, 74, 0.2)',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover:not(:disabled)': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 15px 25px rgba(18, 48, 74, 0.3)',
                        background: 'linear-gradient(135deg, #1a4265 0%, #356792 100%)',
                      },
                      '&:disabled': {
                        background: '#cbd5e1',
                        color: '#f8fafc'
                      }
                    }}
                  >
                    {submitting ? <CircularProgress size={28} color="inherit" /> : "Confirm Selected Time"}
                  </Button>
                </Box>
              )}
            </Stack>
          </Paper>
        </Fade>
      </Container>
    </Box>
  );
};

export default CandidateInterviewSelection;